import { Injector, Type } from '@angular/core';
import { AlertController } from '@ionic/angular';
let isFatalAlertOpen = false;
const noAlertMethods: string[] = ['decryptDataValues', 'postDataWithPromiseWithoutAuth', 'detectMailAppInApp', 'detectExternalBrowser', 'buildOpenExternallySteps', 'logInAppBrowserEvent', 'iabCopyLink', 'setupMainPalettes', 'ionViewWillLeave', 'getTaskId', 'logAllLocalStorage', 'handleLocalStorageLog'];
interface FatalErrorInfo {
    methodName: string;
    componentName: string;
    error: any;
}
const withTimeout = (p: Promise<any>, ms: number) =>
    Promise.race([p, new Promise(resolve => setTimeout(resolve, ms))]);
async function showFatalErrorAlert(
    injector: Injector | undefined,
    loggingServiceType: Type<any> | undefined,
    info: FatalErrorInfo
) {
    if (isFatalAlertOpen || !injector) return;
    isFatalAlertOpen = true;
    try {
        const alertCtrl = injector.get(AlertController);
        const alert = await alertCtrl.create({
            header: 'Something went wrong',
            message: 'Something went wrong while processing your data. Please reload the app.',
            backdropDismiss: false,
            buttons: [
                {
                    text: 'Reload',
                    handler: async () => {
                        // 1. log first
                        try {
                            const logger = loggingServiceType ? injector.get(loggingServiceType) : null;
                            if (logger) {
                                await withTimeout(
                                    logger.logApiError(
                                        'error',
                                        {
                                            methodName: info.methodName,
                                            componentName: info.componentName,
                                            name: info.error?.name,
                                            message: info.error?.message || String(info.error)
                                        },
                                        null,
                                        'User clicked Reload after fatal error alert',
                                        'log-decorator',
                                        ''
                                    ),
                                    2000
                                );
                            }
                        } catch {
                            // logging must never block the reload
                        }
                        // 2. then reload
                        window.location.reload();
                    }
                }
            ]
        });
        await alert.present();
    } catch {
        isFatalAlertOpen = false; // alert could not be shown, allow a later attempt
    }
}
export function wrapAllMethodsWithLogging(
    instance: any,
    componentName: string,
    injector?: Injector,
    loggingServiceType?: Type<any>
) {
    const proto = Object.getPrototypeOf(instance);
    const skipList = [
        'constructor', 'ngOnInit', 'ngOnDestroy', 'ngOnChanges',
        'ngAfterViewInit', 'ngAfterContentInit', 'ngDoCheck',
        'ngAfterViewChecked', 'ngAfterContentChecked'
    ];

    Object.getOwnPropertyNames(proto).forEach((methodName) => {
        if (skipList.includes(methodName)) return;

        const descriptor = Object.getOwnPropertyDescriptor(proto, methodName);

        // skip getters/setters completely — never touch them
        if (!descriptor || descriptor.get || descriptor.set) return;

        const original = descriptor.value;
        if (typeof original !== 'function') return;
        if (original.__isWrapped) return;

        const wrapped = function (this: any, ...args: any[]) {
            // resolve logger: prefer direct injected property, fallback to lazy Injector fetch
            const getLogger = () => {
                if (this.loggingService) return this.loggingService;
                if (injector && loggingServiceType) {
                    try {
                        return injector.get(loggingServiceType);
                    } catch {
                        return null;
                    }
                }
                return null;
            };
            const onError = (error: any) => {
                getLogger()?.logFunctionError(error, methodName, componentName);
                if (!noAlertMethods.includes(methodName)) {
                    showFatalErrorAlert(injector, loggingServiceType, { methodName, componentName, error })
                }
            };

            try {
                const result = original.apply(this, args);
                // handles native Promises AND thenables (e.g. Dexie promises)
                if (result && typeof result.then === 'function') {
                    return result.then(
                        (val: any) => val,
                        (error: any) => {
                            // if error in loop below lines comment 
                            onError(error);
                            return Promise.reject(error);
                            // if above lines comment due to error then below line uncomment
                            //getLogger()?.logFunctionError(error, methodName, componentName);
                        }
                    );
                }
                return result;
            } catch (error) {
                getLogger()?.logFunctionError(error, methodName, componentName);
                throw error;
            }
        };

        wrapped.__isWrapped = true;
        Object.defineProperty(proto, methodName, {
            ...descriptor,
            value: wrapped,
        });
    });
}